/**
 * URL building utility functions
 */

const STORAGE_KEY_OPEN_AVAILABILITY = 'ondiem_open_availability_params';

/** Resolved redirect params, cached because reading them consumes sessionStorage. */
let cachedRedirectParams: URLSearchParams | null = null;

/** Get hash from the window that has the page URL (top frame if we're in an iframe). */
function getPageHash(): string {
  if (typeof window === 'undefined') return '';
  try {
    const top = window.top;
    if (top && top !== window && top.location.hash) {
      return top.location.hash;
    }
  } catch {
    // Cross-origin iframe: use our frame's hash
  }
  return window.location.hash;
}

/** Captured when the script first loads (capture-url.ts) or at module eval; fallback at read time. */
function getCaptured(): { search: string; hash: string } {
  if (typeof window === 'undefined') return { search: '', hash: '' };
  const captured = (window as any).__ondiem_initial_url;
  if (captured && (captured.search !== undefined || captured.hash !== undefined)) {
    return { search: captured.search || '', hash: captured.hash || '' };
  }
  return { search: window.location.search, hash: getPageHash() };
}

/**
 * Store "Show more" redirect params in sessionStorage so the destination page
 * can open the modal even if it strips the URL before our script runs.
 * Call this before navigating (e.g. on Show more click when redirecting).
 */
export function storeOpenAvailabilityParamsForRedirect(fullUrl: string): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    const url = new URL(fullUrl);
    const search = url.search;
    if (search && url.searchParams.get('openAvailability') === '1' && url.searchParams.get('date')) {
      sessionStorage.setItem(STORAGE_KEY_OPEN_AVAILABILITY, search.startsWith('?') ? search.slice(1) : search);
    }
  } catch {
    // ignore
  }
}

/**
 * Query params for opening the modal. Checks, in order:
 * 1. sessionStorage (same-origin redirects only)
 * 2. Initial URL search (when our script loaded)
 * 3. Initial URL hash (cross-origin redirect)
 * 4. Current window.location.hash at read time (in case hash was set after our module ran)
 */
export function getInitialQueryParams(): URLSearchParams {
  // Reading sessionStorage below is destructive, and several callers (attribution,
  // hash-week, modal auto-open, a second widget on the page) each need these params.
  // Cache once we've found a real redirect so the first caller doesn't starve the rest.
  if (cachedRedirectParams) return cachedRedirectParams;

  if (typeof window !== 'undefined' && window.sessionStorage) {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY_OPEN_AVAILABILITY);
      if (stored) {
        sessionStorage.removeItem(STORAGE_KEY_OPEN_AVAILABILITY);
        cachedRedirectParams = new URLSearchParams(stored);
        return cachedRedirectParams;
      }
    } catch {
      // ignore
    }
  }
  const { search: initialSearch, hash: initialHash } = getCaptured();
  const fromSearch = new URLSearchParams(initialSearch);
  if (fromSearch.get('openAvailability') === '1' && fromSearch.get('date')) {
    cachedRedirectParams = fromSearch;
    return fromSearch;
  }
  const liveHash = typeof window !== 'undefined' ? getPageHash() : '';
  for (const rawHash of [initialHash, liveHash]) {
    const hashPart = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash;
    const fromHash = new URLSearchParams(hashPart);
    if (fromHash.get('openAvailability') === '1' && fromHash.get('date')) {
      cachedRedirectParams = fromHash;
      return fromHash;
    }
  }
  // Not a redirect arrival: don't cache, so a hash set after we ran is still picked up.
  return fromSearch;
}

/**
 * Checks if the current page is on an OnDiem-owned domain where the modal
 * should open in-place (no redirect). Includes production, www, and staging Webflow.
 */
export function isOnDiemDomain(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname.toLowerCase();
  return (
    hostname === 'ondiem.com' ||
    hostname === 'www.ondiem.com' ||
    hostname.endsWith('.ondiem.com') ||
    hostname === 'ondiem-staging.webflow.io' // staging: https://ondiem-staging.webflow.io/practices
  );
}

/**
 * Whether to render the partner treatment: the "powered by onDiem" lockup plus
 * the partner style variant. On by default anywhere OnDiem doesn't own the domain
 * — there's no point telling someone they're on onDiem when they already are.
 * data-branding="partner"/"plain" forces it either way (used by the demo pages,
 * which are on netlify.app and would otherwise always get the partner variant).
 */
export function shouldShowPartnerBranding(branding?: 'auto' | 'partner' | 'plain'): boolean {
  if (branding === 'partner') return true;
  if (branding === 'plain') return false;
  return !isOnDiemDomain();
}

/**
 * Gets query parameters from the current URL
 */
export function getQueryParams(): URLSearchParams {
  if (typeof window === 'undefined') return new URLSearchParams();
  return new URLSearchParams(window.location.search);
}

/**
 * Checks if modal should be opened based on query params.
 * Uses the URL as it was when our script loaded (getInitialQueryParams), so we still
 * open the modal even if the host page (e.g. Webflow, SPA) later removes the params from the URL.
 *
 * Returns zip and position as undefined if not in query params.
 */
export function shouldOpenModalFromQuery(): { open: boolean; date?: string; zip?: string; position?: string } {
  const params = getInitialQueryParams();
  const openAvailability = params.get('openAvailability');
  const date = params.get('date');
  
  // Return undefined if param doesn't exist, empty string if it exists but is empty
  const zip = params.has('zip') ? (params.get('zip') || '') : undefined;
  const position = params.has('position') ? (params.get('position') || '') : undefined;
  
  return {
    open: openAvailability === '1' && !!date,
    date: date || undefined,
    zip,
    position,
  };
}

export function buildProfileUrl(
  userUUID: string,
  utmContent: string = 'hire_a_pro'
): string {
  const base = `https://app.ondiem.com/profile/${encodeURIComponent(userUUID)}`;
  const params = new URLSearchParams({
    utm_medium: 'website',
    utm_campaign: 'pro_availability_2025',
    utm_content: utmContent,
    utm_term: 'profile_click',
  });
  return `${base}?${params.toString()}`;
}

/**
 * Builds the "Show more" redirect URL. Params are put in the **hash** (fragment) so they
 * survive cross-origin redirects and aren't stripped by the destination (e.g. Webflow).
 * sessionStorage cannot be read on a different origin.
 */
export function buildShowMoreUrl(
  baseUrl: string,
  date: string,
  zip?: string | null,
  position?: string | null,
  instance?: string,
  utmContent?: string
): string {
  try {
    const url = new URL(baseUrl);
    const params = new URLSearchParams();
    params.set('openAvailability', '1');
    params.set('date', date);
    if (zip) params.set('zip', zip);
    if (position) params.set('position', position);
    if (instance && instance !== 'your-instance-name' && instance !== 'default') {
      params.set('instance', instance);
    }
    if (utmContent && utmContent !== 'your_utm_content') {
      params.set('utm_content', utmContent);
    }
    url.hash = params.toString();
    return url.toString();
  } catch {
    return baseUrl;
  }
}

/**
 * Attribution params carried over from a partner site's "Show more" redirect.
 * buildShowMoreUrl() writes `instance` and `utm_content` into the hash; without this
 * the destination widget would tag its events with its own data-instance and the
 * partner attribution would be lost on arrival.
 *
 * Only returns values on an actual redirect arrival (openAvailability=1 + date), so a
 * stray `instance` param on a normal page load can't override the embed's own config.
 */
export function getRedirectAttribution(): { instance?: string; utmContent?: string } {
  const params = getInitialQueryParams();
  if (params.get('openAvailability') !== '1' || !params.get('date')) return {};

  const instance = params.get('instance');
  const utmContent = params.get('utm_content');

  return {
    instance: instance && instance !== 'your-instance-name' ? instance : undefined,
    utmContent: utmContent && utmContent !== 'your_utm_content' ? utmContent : undefined,
  };
}
